import { useState, useEffect, useRef } from 'react';
import { Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, IconButton, Stack, Menu, MenuItem, TextField } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityOnIcon from '@mui/icons-material/Visibility';
import axios from '../../api/axios';
import { WithPermission } from '../../Auth/WithPermission';

const GET_REGISTERED_DEVICE = "/registeredDevice/"

const RegisteredDevices = () => {
  const [allDevices, setAllDevices] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [nameError, setNameError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    axios.post(GET_REGISTERED_DEVICE, {
      token: localStorage.getItem('token'),
      returnListOfRegisteredDevices: true
    }).then((response) => {
      if (response.data?.code === 0) {
        let tempAllDevices = response.data?.message?.listOfRegisteredDevices;
        tempAllDevices = tempAllDevices.map(code => ({
          ...code,
          removed: new Date(code.removedDate) < new Date(),
          status: code.removedDate === "" && new Date(code.tokenExpDate) > new Date() ? "Active" : code.removedDate === "" && new Date(code.tokenExpDate) < new Date() ? "Update Required" : code.removedDate !== "" ? "Removed" : ""
        }));
        console.log(tempAllDevices)
        setAllDevices(tempAllDevices);
        updateFilteredDevices(tempAllDevices, showRemoved);
      } else if (response.data?.code === 328) {
        console.log(response.data?.message);
      } else {
        console.log(response);
      }
    })
    .catch((error) => {
      console.error('Error retrieving registration codes:', error);
    });
  }, []);

  useEffect(() => {
    if (editingDeviceId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingDeviceId]);

  const handleMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  // Sort devices by ocubuildDeviceName, accounting for embedded numbers
  const sortDevices = (devices) => {
    return [...devices].sort((a, b) => {
      const nameA = a?.ocubuildDeviceName?.trim();
      const nameB = b?.ocubuildDeviceName?.trim();

      // if both missing -> equal
      if (!nameA && !nameB) return 0;

      // if only A missing -> push A down
      if (!nameA) return 1;

      // if only B missing -> push B down
      if (!nameB) return -1;

      // both exist -> normal compare
      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  };

  // Then call this before setting filteredCodes
  const updateFilteredDevices = (allDevicesList, showRemovedFlag) => {
    const visibleDevices = allDevicesList.filter(device => showRemovedFlag || !device.removed);
    const sortedDevices = sortDevices(visibleDevices);
    setFilteredDevices(sortedDevices);
  };

  function updateRemoved(regDeviceId) {
    removeRegisteredDevice(regDeviceId);
    setAnchorEl(null);
    setMenuIndex(null);
  };

  function removeRegisteredDevice(regDeviceId) {
    axios.post(GET_REGISTERED_DEVICE, {
      token: localStorage.getItem('token'),
      updateRemovedRegDevice: true,
      regDeviceId: regDeviceId
    }).then((response) => {
      if (response.data?.code === 0) {
        console.log(response.data?.message?.updated)
        const date = new Date().toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

        let tempAllDevices = allDevices.map(device =>
          device.id === regDeviceId
            ? { ...device, removed: date }
            : device
        );

        setAllDevices(tempAllDevices);
        updateFilteredDevices(tempAllDevices, showRemoved)
      } else if (response.data?.code === 416) {
        console.log(response.data?.message);
      } else {
        console.log(response)
      }
    })
    .catch((error) => {
      console.error('Updating registration code expired status:', error);
    });
  }

  const handleToggleShowRemoved = () => {
    const newShowRemoved = !showRemoved;
    setShowRemoved(newShowRemoved);
    updateFilteredDevices(allDevices, newShowRemoved);
  };

  const handleRenameClick = (device) => {
    setEditingDeviceId(device.id);
    setEditingName(device.ocubuildDeviceName);
    setNameError('');
    handleClose();
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) {
      setEditingName(value);
      setNameError('');
    }
  };

  const isNameUnique = (newName, deviceId) => {
    // Check if name is unique across non-removed devices (excluding current device)
    return !allDevices.some(device => 
      device.id !== deviceId && 
      device.ocubuildDeviceName.toLowerCase() === newName.toLowerCase() && 
      !device.removedDate
    );
  };

  const handleNameSave = () => {
    const trimmedName = editingName.trim();
    
    // If blank, revert to original name
    if (trimmedName === '') {
      const originalDevice = allDevices.find(d => d.id === editingDeviceId);
      setEditingName(originalDevice.ocubuildDeviceName);
      setEditingDeviceId(null);
      setNameError('');
      return;
    }

    // Check if name is unique
    if (!isNameUnique(trimmedName, editingDeviceId)) {
      setNameError('That name has already been used on another device, please choose a unique device name.');
      return;
    }

    // Save the new name
    axios.post(GET_REGISTERED_DEVICE, {
      token: localStorage.getItem('token'),
      updateDeviceName: true,
      regDeviceId: editingDeviceId,
      newDeviceName: trimmedName
    }).then((response) => {
      console.log(response)
      if (response.data?.code === 0) {
        // Update local state
        let tempAllDevices = allDevices.map(device =>
          device.id === editingDeviceId
            ? { ...device, ocubuildDeviceName: trimmedName }
            : device
        );
        
        setAllDevices(tempAllDevices);
        updateFilteredDevices(tempAllDevices, showRemoved);
        setEditingDeviceId(null);
        setNameError('');
      } else {
        console.log(response);
        setNameError('Error updating device name. Please try again.');
      }
    })
    .catch((error) => {
      console.log('Error updating device name:', error);
      setNameError('Error updating device name. Please try again.');
    });
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setEditingDeviceId(null);
      setEditingName('');
      setNameError('');
    }
  };

  const handleNameBlur = () => {
    handleNameSave();
  };

  const formatDateCreated = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Registered Devices
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        Remove devices or reconnect devices that lost their connection. To add a device, create a code in Registration Codes above.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" mt={2} mb={2}>
        <Button
          variant="text"
          startIcon={showRemoved ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
          sx={{ color: '#1a73e8' }}
          onClick={handleToggleShowRemoved}
        >
          {showRemoved ? 'HIDE REMOVED' : 'SHOW REMOVED'}
        </Button>
      </Stack>

      <TableContainer elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Device Name</strong></TableCell>
              <TableCell><strong>Model</strong></TableCell>
              <TableCell><strong>OS Version</strong></TableCell>
              <TableCell><strong>Last Update</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices.map((device, index) => (
              <TableRow key={index}>
                <TableCell sx={device.removed ? { color: 'text.disabled' } : {}}>
                  {editingDeviceId === device.id ? (
                    <div>
                      <TextField
                        inputRef={inputRef}
                        value={editingName}
                        onChange={handleNameChange}
                        onKeyDown={handleNameKeyDown}
                        onBlur={handleNameBlur}
                        error={!!nameError}
                        helperText={nameError}
                        size="small"
                        fullWidth
                        variant='standard'
                        inputProps={{ maxLength: 50 }}
                      />
                    </div>
                  ) : (
                    device.ocubuildDeviceName
                  )}
                </TableCell>
                <TableCell sx={device.removed ? { color: 'text.disabled' } : {}}>{device.deviceModel}</TableCell>
                <TableCell sx={device.removed ? { color: 'text.disabled' } : {}}>{device.systemVersion}</TableCell>
                <TableCell sx={device.removed ? { color: 'text.disabled' } : {}}>{formatDateCreated(device.lastSyncDateTime)}</TableCell>
                <TableCell sx={device.removed ? { color: 'text.disabled' } : {}}>
                  <Tooltip
                    title={
                      device.status === "Update Required" ? `Device needs to go online to get updates and become Active again.` :
                        device.status === "Lost Connection" ? `Use the menu on the right to Reconnect or Remove Device.` : ""
                    }
                    arrow
                  >
                    <span>{device.status}</span>
                  </Tooltip>
                </TableCell>
                <WithPermission rolesRequired={['admin', 'superadmin']}>
                  <TableCell align="right">
                    {!device.removed && (
                      <>
                        <IconButton size="small" onClick={(e) => handleMenuClick(e, index)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && menuIndex === index}
                          onClose={handleClose}
                        >
                          <MenuItem onClick={() => handleRenameClick(device)}>
                            Rename
                          </MenuItem>
                          <MenuItem onClick={() => updateRemoved(device.id)} sx={{ color: "red" }}>
                            Remove Device
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </TableCell>
                </WithPermission>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default RegisteredDevices;